import React, { useEffect, useState } from "react";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import GetAppIcon from "@mui/icons-material/GetApp";
import GroupsIcon from "@mui/icons-material/Groups";
import ImageIcon from "@mui/icons-material/Image";
import DownloadActivityIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { Avatar, AvatarGroup, Button, Stack } from "@mui/material";
import { StatusComponentGuard } from "components";
import { DealRoleGuard } from "components/guards";
import { Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useDealParamsHooks, useModals, useNavigate, useRoleGuardHook } from "hooks";
import {
  useDealActions,
  useDealSelector,
  useDealSharesSelect,
  useOpportunityIdSelector,
  usePickUserPermissions,
  useTaskManageActions,
  useUserIdSelector,
  useUserRoleSelector,
  useViewActions
} from "store";
import { IDashboardShare, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";
import { ChangeLogoModal } from "../Change-logo-modal/ChangeLogoModal";
import { RenameModal } from "../Rename-modal/RenameModal";
import { Status } from "../Status/Status";

export const DealManage: React.FC = () => {
  const [isRename, setIsRename] = useState<boolean>(false);
  const [isChangeLogo, setIsChangeLogo] = useState<boolean>(false);
  const { id, title, user_id } = useDealSelector();
  const { dealId } = useDealParamsHooks();
  const userId = useUserIdSelector();
  const role = useUserRoleSelector();
  const shares = useDealSharesSelect();
  const navigate = useNavigate();
  const { investorVisualizationModal, errorModal, deleteModal } = useModals();
  const opportunityId = useOpportunityIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const { getDealsAction } = useTaskManageActions();
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const {
    deleteDealAction,
    exportDealAction,
    setHierarchyPanelOpenAction,
    exportDealActivityAction,
    downloadUnsavedFilesAction,
    getDashboardSharesAction
  } = useDealActions();
  const { setShareDialog } = useViewActions();

  const { action: deleteAction } = useActionHook(deleteDealAction, {
    onSuccess: getDealsAction
  });

  useEffect(() => {
    dealId && getDashboardSharesAction(dealId, false);
  }, []);

  const isDeleteAvailableForUser = () => {
    if ((userId === user_id || role?.name === RoleName.Admin) && onClickDelete) {
      return onClickDelete(id, title);
    }
  };

  const onClickDelete = useRoleGuardHook(
    (id: number, title: string) => {
      deleteModal({
        action: async () => {
          await deleteAction(id);
          navigate({ route: "myDeals" });
        },
        element: "Delete",
        message: `Are you sure you want to delete ${title}?`
      });
    },
    [RoleName.Admin, RoleName.Analyst]
  );

  return (
    <Stack direction="row" alignItems="center" gap="16px">
      <StatusComponentGuard statuses={[opportunityId]} show={false}>
        <AvatarGroup
          sx={{
            "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 }
          }}
          max={3}
        >
          {shares?.length &&
            shares.map((share: IDashboardShare, index: number) => (
              <MaterialTooltip isShow key={share.id + index} placement="top" title={share.full_name}>
                <Avatar src={share.profile_photo_url} />
              </MaterialTooltip>
            ))}
        </AvatarGroup>
      </StatusComponentGuard>
      <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        {!noAccessPermission && !isViewerPermission && (
          <StatusComponentGuard statuses={[opportunityId]} show={false}>
            <Button
              sx={{ padding: "0px 8px", minWidth: "85px", maxWidth: "85px", height: "24px", minHeight: "24px" }}
              variant="outlined"
              onClick={() =>
                setShareDialog({
                  elements: [id],
                  type: "deal_id",
                  shareName: title,
                  dealId: id,
                  itemToShare: null
                })
              }
            >
              <PersonAddAltIcon sx={{ color: "#0a3142", width: "20px", marginRight: "5px" }} />
              <p>Share</p>
            </Button>
          </StatusComponentGuard>
        )}
        <Status />
      </DealRoleGuard>
      <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        <StatusComponentGuard statuses={[opportunityId]} show={false}>
          <Menu
            hidden={noAccessPermission || isViewerPermission}
            options={[
              {
                title: "Rename Deal",
                icon: <CreateIcon />,
                onClick: () => setIsRename(true)
              },
              {
                title: "Edit Logo",
                icon: <ImageIcon />,
                onClick: () => setIsChangeLogo(true)
              },
              {
                title: "Export Deal",
                icon: <GetAppIcon />,
                onClick: () => exportDealAction(id)
              },
              {
                title: "Download Unsaved Files",
                icon: <GetAppIcon />,
                onClick: () =>
                  downloadUnsavedFilesAction(id).catch((error) => {
                    error.response.text().then((value: any) => {
                      errorModal({ msg: JSON.parse(value)?.message || "Unknown Error" });
                    });
                  })
              },
              {
                title: "Deal Hierarchy",
                icon: <AccountTreeIcon />,
                onClick: () => setHierarchyPanelOpenAction(true)
              },
              {
                title: "Delete Deal",
                icon: <DeleteIcon />,
                onClick: isDeleteAvailableForUser,
                hidden: userId !== user_id && role?.name !== RoleName.Admin
              },
              {
                title: "Download Sharing Info",
                icon: <DownloadActivityIcon />,
                onClick: () => exportDealActivityAction(id),
                hidden: role?.name !== RoleName.Admin
              },
              {
                title: "View Hierarchy Visualization",
                icon: <GroupsIcon />,
                onClick: () => investorVisualizationModal({ isOpen: true, dealId }),
                hidden: !isRoleAnalyst({ role })
              }
            ]}
          />
        </StatusComponentGuard>
      </DealRoleGuard>
      <RenameModal isOpen={isRename} close={() => setIsRename(false)} />
      <ChangeLogoModal isChangeLogo={isChangeLogo} close={() => setIsChangeLogo(false)} />
    </Stack>
  );
};
